import SpacingControls from '../../utils/SpacingControls';
import { getSpacingClasses } from '../../utils/getSpacingClasses';
import { useSpacingOptions, createResetSpacingFunction } from '../../utils/spacing-utils';
import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  MediaUpload,
  InnerBlocks,
  BlockControls,
  InspectorControls,
  ColorPalette,
  __experimentalLinkControl as LinkControl,
} from '@wordpress/block-editor';
import {
  Button,
  ToolbarGroup,
  ToolbarButton,
  PanelBody,
  Tooltip,
  Icon
} from '@wordpress/components';
import { Fragment, useState } from '@wordpress/element';
import {
  MdSwapHoriz,
  MdViewColumn,
  MdViewList,
} from 'react-icons/md';
import { upload, link } from "@wordpress/icons";

const ALLOWED_BLOCKS = [
  'ensoul/button',
  'ensoul/heading',
  'ensoul/paragraph',
  'ensoul/divider',
];
const TEMPLATE = [
  ['ensoul/button', { placeholder: 'Add button...' }],
  ['ensoul/paragraph', { placeholder: 'Add paragraph...' }],
  ['ensoul/heading', { placeholder: 'Add heading...' }],
];

export default function Edit({ attributes, setAttributes, context }) {
  const {
    orientation,
    invertLayout,
    imageURL,
    contentBackgroundColor,
    href, 
    opensInNewTab
  } = attributes;

  const spacingOptions = useSpacingOptions();

  // Create reset spacing function
  const resetSpacing = createResetSpacingFunction(setAttributes);

  /* const pagination = context['ensoul/content-slider/pagination'];
  const totalSlides = context['ensoul/content-slider/totalSlides'];
  const activeIndex = context['ensoul/content-slider/activeIndex']; */
  
  const blockProps = useBlockProps({
    className: `swiper-slide !h-auto ${getSpacingClasses(attributes)}`,
  });

  const isVertical = orientation === 'vertical';
  const containerClasses = `card-slider flex ${ isVertical ? invertLayout ? 'flex-col-reverse' : 'flex-col' : invertLayout ? 'flex-row-reverse' : 'flex-row'}`;
  const contentWrapperClasses = `mb-auto ${isVertical ? 'w-full' : 'md:w-1/2 w-full'}`;
  const imageWrapperClasses = `image_wrapper flex justify-center relative w-full ${isVertical ? '' : 'md:w-1/2'}`;
  const [isLinkPickerVisible, setIsLinkPickerVisible] = useState(false);

  const handleContentBackgroundColorChange = (color) => {
    setAttributes({ contentBackgroundColor: color || '' });
  };

  // Handle link addition
	const handleLinkChange = (newLink) => {
		setAttributes({
			href: newLink?.url || "",
			opensInNewTab: newLink?.opensInNewTab || false,
		});
		setIsLinkPickerVisible(false); // Close the link picker after selection
	};

  return (
    <Fragment>
      <BlockControls>
        <ToolbarGroup>
          <ToolbarButton
            icon={link}
            label="Add Link"
            onClick={() => setIsLinkPickerVisible(!isLinkPickerVisible)}
            className={`${
              href ? "before:bg-black [&_svg]:fill-white" : "bg-transparent"
            }`}
          />
          <Tooltip text={__('Toggle Orientation', 'ensoul-wp-blocks')}>
            <Button
              icon={
                isVertical ? <MdViewList size={20} /> : <MdViewColumn size={20} />
              }
              onClick={() =>
                setAttributes({
                  orientation: isVertical ? 'horizontal' : 'vertical',
                })
              }
            />
          </Tooltip>

          <Tooltip text={__('Toggle Layout', 'ensoul-wp-blocks')}>
            <Button
              icon={<MdSwapHoriz size={20} />}
              onClick={() => setAttributes({ invertLayout: !invertLayout })}
            />
          </Tooltip>
        </ToolbarGroup>

        {/* Link Picker */}
        {isLinkPickerVisible && (
          <div
            className="absolute top-full left-0 bg-white p-2 [&_input]:z-10 [&_button]:z-10"
          >
            <LinkControl
              value={{ url: href, opensInNewTab }}
              onChange={handleLinkChange}
            />
            <div className="flex justify-between mt-2">
              <Button
                variant="secondary"
                onClick={() => setIsLinkPickerVisible(false)}
              >
                Close
              </Button>
              {href && (
                <Button
                  variant="secondary"
                  isDestructive
                  onClick={() => {
                    setAttributes({ href: "", opensInNewTab: false });
                  }}
                >
                  Clear Link
                </Button>
              )}
            </div>
          </div>
        )}
      </BlockControls>

      <InspectorControls>
        <PanelBody
          title={__('Content Background Color', 'ensoul-wp-blocks')}
          initialOpen={false}
        >
          <ColorPalette
            value={contentBackgroundColor}
            onChange={handleContentBackgroundColorChange}
            allowReset
          />
          {contentBackgroundColor && (
            <Button
              onClick={() => setAttributes({ contentBackgroundColor: '' })}
              isDestructive
              style={{ marginTop: '10px' }}
            >
              {__('Remove Background Color', 'ensoul-wp-blocks')}
            </Button>
          )}
        </PanelBody>
        <SpacingControls
          attributes={attributes}
          setAttributes={setAttributes}
          spacingOptions={spacingOptions}
          resetSpacing={resetSpacing}
        />
      </InspectorControls>

      <div {...blockProps}>
        <div className={containerClasses}>
          <div className={imageWrapperClasses}>
            <MediaUpload
              onSelect={(media) =>
                setAttributes({
                  imageURL: media.url,
                })
              }
              allowedTypes={["image"]}
              render={({ open }) => (
                <>
                  {imageURL ? (
                    <>
                      <img src={imageURL} alt="" className="w-full !h-auto" />
                      <Button onClick={open} variant="primary" className="absolute z-10 right-0" icon="edit" />
                    </>
                  ) : (
                    <Button onClick={open} icon={<Icon icon={upload} />} variant="primary">
                      Upload Image
                    </Button>
                  )}
                </>
              )}
            />
          </div>

          <div
          className={contentWrapperClasses}
            style={
              contentBackgroundColor
                ? { backgroundColor: contentBackgroundColor }
                : {}
            }
          >
            <InnerBlocks
              allowedBlocks={ALLOWED_BLOCKS}
              template={TEMPLATE}
              templateLock={false}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
}
